import { FC, useMemo, useState } from 'react';

import { AnyFCO, DatasetFCO, DatasetFCOFields, FCOFields } from '../../../core/types/fcoTypes';

import { uniq } from 'lodash';
import {
  ComboFilterCategory,
  ComboFilterListSelector,
  EmptyProps,
  ExtendedTableColumn,
  useTableFilters,
} from '@lithos';
import { Link, Select, Table, TableViewLayout, Tooltip } from '@lithos';
import { getFilteredItems, tableArrayLengthSortParameters, tableStringSortParameters } from '@lithos/TableUtils';
import TableSelectors from '../fcoListView/TableSelectors';
import { EmptyValue } from '@tecton/ComponentRedesign/lib';
import TableColumnControls from '../fcoListView/TableColumnControls';

interface DataSetsListsProps {
  items: DatasetFCO[];
  idToFcoMap: Record<string, AnyFCO>;
  workspace: string;
}

enum DatasetListColumnKeys {
  NAME,
  TYPE,
  FEATURE_VIEWS,
  FEATURE_SERVICES,
}

interface DatasetListFilterState {
  type: string[];
  'Feature Views': string[];
  'Feature Services': string[];
}

export interface DatasetFilterStateType {
  Type: string[];
  'Feature Views': string[];
  'Feature Services': string[];
}

const DataSetsList: FC<DataSetsListsProps> = ({ items, idToFcoMap, workspace }) => {
  const defaultFilterState: DatasetListFilterState = useMemo(
    (): DatasetListFilterState => ({
      type: [],
      'Feature Views': [],
      'Feature Services': [],
    }),
    []
  );

  const allValidOptions = useMemo(() => {
    return {
      Type: uniq(items.map((item) => item[DatasetFCOFields.TYPE])).flat(),
      'Feature Views': uniq(items.map((item) => item[DatasetFCOFields.FEATURE_VIEW_NAME]))
        .flat()
        .filter((name) => name !== undefined),
      'Feature Services': uniq(items.map((item) => item[DatasetFCOFields.FEATURE_SERVICE_NAME]))
        .flat()
        .filter((name) => name !== undefined),
    };
  }, [items]);

  const {
    searchQuery,
    hasAnyFilters,
    activeFilterCount,
    sortType,
    setSortType,
    activeHeading,
    setActiveHeading,
    filterState,
    onSearchInputChange,
    handleResetFilters,
    handleFilterChange,
  } = useTableFilters<DatasetFCO, DatasetListColumnKeys, DatasetListFilterState>(defaultFilterState);

  const filterComponents: Record<
    DatasetListColumnKeys,
    {
      filter: React.ReactNode;
      activeCount: () => number;
      filterCallback: (dataset: DatasetFCO) => boolean;
      name: string;
    }
  > = {
    [DatasetListColumnKeys.NAME]: {
      filter: <></>,
      activeCount: () => 0,
      filterCallback: () => true,
      name: 'Name',
    },
    [DatasetListColumnKeys.TYPE]: {
      filter: (
        <ComboFilterListSelector
          selected={filterState.type}
          setSelected={(type) => handleFilterChange(filterComponents, 'type', type)}
          icon={'Dataset'}
          items={allValidOptions['Type']}
          title={''}
        />
      ),
      activeCount: () => filterState.type.length,
      filterCallback: (dataset) => {
        return filterState.type.includes(dataset[DatasetFCOFields.TYPE]);
      },
      name: 'Type',
    },
    [DatasetListColumnKeys.FEATURE_VIEWS]: {
      filter: (
        <ComboFilterListSelector
          selected={filterState['Feature Views']}
          setSelected={(fv) => handleFilterChange(filterComponents, 'Feature Views', fv)}
          icon={'FeatureView'}
          items={allValidOptions['Feature Views'] as string[]}
          title={''}
        />
      ),
      activeCount: () => filterState['Feature Views'].length,
      filterCallback: (dataset) => {
        return filterState['Feature Views'].includes(dataset[DatasetFCOFields.FEATURE_VIEW_NAME] ?? '');
      },
      name: 'Feature Views',
    },
    [DatasetListColumnKeys.FEATURE_SERVICES]: {
      filter: (
        <ComboFilterListSelector
          selected={filterState['Feature Services']}
          setSelected={(fs) => handleFilterChange(filterComponents, 'Feature Services', fs)}
          icon={'FeatureService'}
          items={allValidOptions['Feature Services'] as string[]}
          title={''}
        />
      ),
      activeCount: () => filterState['Feature Services'].length,
      filterCallback: (dataset) => {
        return filterState['Feature Services'].includes(dataset[DatasetFCOFields.FEATURE_SERVICE_NAME] ?? '');
      },
      name: 'Feature Services',
    },
  };

  const categories: ComboFilterCategory[] = [
    {
      name: 'Type',
      icon: 'Dataset',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'type', defaultFilterState.type);
      },
      activeCount: filterComponents[DatasetListColumnKeys.TYPE].activeCount(),
      popover: filterComponents[DatasetListColumnKeys.TYPE].filter,
      disabled: allValidOptions['Type'].length === 0,
    },
    {
      name: 'Feature Views',
      icon: 'FeatureView',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'Feature Views', defaultFilterState['Feature Views']);
      },
      activeCount: filterComponents[DatasetListColumnKeys.FEATURE_VIEWS].activeCount(),
      popover: filterComponents[DatasetListColumnKeys.FEATURE_VIEWS].filter,
      disabled: allValidOptions['Feature Views'].length === 0,
    },
    {
      name: 'Feature Services',
      icon: 'FeatureService',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'Feature Services', defaultFilterState['Feature Services']);
      },
      activeCount: filterComponents[DatasetListColumnKeys.FEATURE_SERVICES].activeCount(),
      popover: filterComponents[DatasetListColumnKeys.FEATURE_SERVICES].filter,
      disabled: allValidOptions['Feature Services'].length === 0,
    },
  ];

  const columns: ExtendedTableColumn<DatasetFCO>[] = [
    {
      name: 'Name',
      id: DatasetListColumnKeys.NAME,
      content: (dataset) => (
        <Link
          name={dataset[FCOFields.NAME]}
          icon="Dataset"
          url={`/repo/${workspace}/datasets/${dataset[FCOFields.NAME]}/overview`}
        />
      ),
    },
    {
      name: 'Type',
      id: DatasetListColumnKeys.TYPE,
      content: (dataset) => <>{dataset[DatasetFCOFields.TYPE]}</>,
      filterContent: filterComponents[DatasetListColumnKeys.TYPE].filter,
      ...tableStringSortParameters(sortType, DatasetFCOFields.TYPE, setSortType),
    },
    {
      name: 'Feature Views',
      id: DatasetListColumnKeys.FEATURE_VIEWS,
      content: (dataset) => {
        const featureViewId = dataset[DatasetFCOFields.FEATURE_VIEW_ID];

        if (!dataset[DatasetFCOFields.FEATURE_VIEW_ID]) {
          return <EmptyValue />;
        }

        const isModified = !Object.keys(idToFcoMap).includes(dataset[DatasetFCOFields.FEATURE_VIEW_ID]);
        const fco = idToFcoMap[featureViewId ?? ''];

        return (
          <>
            {isModified ? (
              <Tooltip
                trigger={
                  <Link disabled={true} name={dataset[DatasetFCOFields.FEATURE_VIEW_NAME] ?? ''} icon={'FeatureView'} />
                }
                content={<div>This object has been modified or deleted.</div>}
              />
            ) : (
              <Link
                url={`/repo/${workspace}/features/${encodeURIComponent(fco[FCOFields.NAME] ?? '')}`}
                icon="FeatureView"
                name={fco[FCOFields.NAME] ?? ''}
              />
            )}
          </>
        );
      },
      filterContent: filterComponents[DatasetListColumnKeys.FEATURE_VIEWS].filter,
      ...tableArrayLengthSortParameters(sortType, DatasetFCOFields.FEATURE_VIEW_ID, setSortType),
    },
    {
      name: 'Feature Services',
      id: DatasetListColumnKeys.FEATURE_SERVICES,
      content: (dataset) => {
        if (!dataset[DatasetFCOFields.FEATURE_SERVICE_ID]) {
          return <EmptyValue />;
        }

        const isModified = !Object.keys(idToFcoMap).includes(dataset[DatasetFCOFields.FEATURE_SERVICE_ID]);
        // We need to escape the workspace name since we can have unfriendly names for FCOS for example "samantha:parallel_dataset:V1:3"
        const urlEncodedFcoName = encodeURIComponent(dataset[DatasetFCOFields.FEATURE_SERVICE_NAME] ?? '');

        return isModified ? (
          <Tooltip
            trigger={
              <Link
                disabled={true}
                name={dataset[DatasetFCOFields.FEATURE_SERVICE_NAME] ?? ''}
                icon={'FeatureService'}
              />
            }
            content={<div>This object has been modified or deleted.</div>}
          />
        ) : (
          <Link
            url={`/repo/${workspace}/feature-services/${urlEncodedFcoName}`}
            icon="FeatureService"
            name={idToFcoMap[dataset[DatasetFCOFields.FEATURE_SERVICE_ID]][FCOFields.NAME] ?? ''}
          />
        );
      },
      filterContent: filterComponents[DatasetListColumnKeys.FEATURE_SERVICES].filter,
      ...tableArrayLengthSortParameters(sortType, DatasetFCOFields.FEATURE_SERVICE_ID, setSortType),
    },
  ];

  const selectableColumns = columns.map((column) => {
    return { name: column.name as string, visible: true };
  });

  const [columnState, setColumnState] = useState<{ name: string; visible: boolean }[]>(selectableColumns);

  const columnsSelector = (
    <Select
      groups={[
        {
          items: columnState.map((column) => {
            return {
              name: column.name,
              asToggle: true,
              onClick: () => {
                const newColumnState = columnState.map((selectableColumn) => {
                  if (selectableColumn.name !== column.name) {
                    return selectableColumn;
                  }

                  return {
                    name: selectableColumn.name,
                    visible: !selectableColumn.visible,
                  };
                });
                setColumnState && setColumnState(newColumnState);
              },
              selected: column.visible,
              selectable: true,
            };
          }),
        },
      ]}
      filterable={false}
    />
  );

  const noResultsEmpty: EmptyProps = {
    actions: <></>,
    placement: 'full-page',
    useCase: 'empty',
    title: 'No Datasets',
    description: 'No Datasets have been generated.',
  };

  const filterEmpty: EmptyProps = {
    actions: <></>,
    placement: 'full-page',
    useCase: 'empty',
    title: 'No Results',
    description: 'No Datasets match the current filters. Try adjusting your filters or search query.',
  };

  const filteredColumns = columns
    .map((column) => {
      const matching = columnState.find((stateColumn) => stateColumn.name === column.name);
      if (!matching) {
        return;
      }
      if (matching?.visible !== true) {
        return;
      }

      const asColumn: ExtendedTableColumn<DatasetFCO> = {
        ...column,
        isActive: column.id === activeHeading,
        setIsActive: (arg) => {
          if (arg) {
            setActiveHeading(column.id as DatasetListColumnKeys);
            return;
          }
          setActiveHeading(undefined);
        },
        hideCallback: () => {
          const newColumnState = columnState.map((selectableColumn) => {
            if (selectableColumn.name !== column.name) {
              return selectableColumn;
            }

            return {
              name: selectableColumn.name,
              visible: !selectableColumn.visible,
            };
          });

          setColumnState && setColumnState(newColumnState);
        },
        columnsContent: columnsSelector,
      };

      return asColumn;
    })
    .filter((column) => column !== undefined) as ExtendedTableColumn<DatasetFCO>[];

  const activeFilters = Object.values(filterComponents).filter((filter) => {
    return filter.activeCount() > 0;
  });

  const filteredResults = getFilteredItems(
    items,
    activeFilters,
    [FCOFields.NAME, FCOFields.ID],
    searchQuery ?? '',
    sortType
  );

  return (
    <TableViewLayout
      searchInput={
        <TableSelectors
          hasAnyFilters={hasAnyFilters}
          activeFilterCount={activeFilterCount}
          handleResetFilters={handleResetFilters}
          categories={categories}
          onSearchInputChange={onSearchInputChange}
        />
      }
      filterAndColumnControls={<TableColumnControls columnsSelector={columnsSelector} />}
      table={
        <Table
          items={filteredResults}
          columns={filteredColumns}
          frozenFirstColumn={true}
          emptyProps={items.length === 0 ? noResultsEmpty : filterEmpty}
          memoized={true}
        />
      }
    />
  );
};

export default DataSetsList;
